import { type ReactNode, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LogIn, Lock, RefreshCw, KeyRound } from "lucide-react";
import { useMe } from "@/shared/api/hooks";
import { ApiError, api } from "@/shared/api/client";

const SSO_REFRESH = "https://team.wowblogger.com/sso/refresh?next=https://lk2.wowblogger.com/account/projects";

export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();

  if (me.isLoading) return <Splash />;
  if (me.isError) {
    const status = me.error instanceof ApiError ? me.error.status : 0;
    if (status === 401 || status === 403) return <LoginGate status={status} />;
  }
  if (me.data) return <>{children}</>;
  return <LoginGate status={0} />;
}

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center bg-ink text-white/60 text-[14px]">
      Загрузка…
    </div>
  );
}

function LoginGate({ status }: { status: number }) {
  const noAccess = status === 403;
  return (
    <div className="min-h-screen grid place-items-center bg-ink text-white relative overflow-hidden">
      <main className="text-center max-w-[560px] px-6 fade-up relative z-10">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 border border-white/15 mb-6">
          <Lock size={22} />
        </div>
        <h1 className="font-display font-black uppercase tracking-[-0.02em] text-[44px] leading-[1.05] mb-4">
          {noAccess ? "ДОСТУП ЗАКРЫТ" : "ВХОД ПО ПРИГЛАШЕНИЮ"}
        </h1>
        {noAccess ? (
          <>
            <p className="text-[15px] text-white/65 leading-snug mb-3">
              У вашего текущего сеанса нет роли в сервисе lk2. Если доступ был выдан недавно — сначала обновите cookie через WOWTeam.
            </p>
            <p className="text-[13px] text-white/45 leading-snug mb-8">
              Не помогло? Напишите Максиму Горбачёву — выдадим роль вручную.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <a href={SSO_REFRESH}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[15px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] transition-colors">
                <RefreshCw size={18} strokeWidth={2.5} /> Обновить сессию
              </a>
              <a href="https://team.wowblogger.com/logout?next=https://lk2.wowblogger.com/"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-pill bg-white/10 border border-white/20 text-white font-display font-semibold text-[15px] hover:bg-white/15">
                Войти под другой учётной
              </a>
            </div>
            <RespondentCodeBox />
          </>
        ) : (
          <>
            <p className="text-[15px] text-white/65 leading-snug mb-8">
              Прототип WOWBLOGGER Cabinet 2.0 — закрытый. Войдите через WOWTeam, чтобы продолжить.
            </p>
            <a href={SSO_REFRESH}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[15px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] transition-colors">
              <LogIn size={18} strokeWidth={2.5} /> Войти через WOWTeam
            </a>
            <RespondentCodeBox />
          </>
        )}
        <div className="mt-10 text-[12px] text-white/40">
          WOWBLOGGER · lk2.wowblogger.com
        </div>
      </main>
      <div className="absolute top-[-100px] right-[-100px] w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,115,219,0.22) 0%, transparent 60%)" }} />
      <div className="absolute bottom-[-80px] left-[-80px] w-[260px] h-[260px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(191,249,63,0.18) 0%, transparent 60%)" }} />
    </div>
  );
}

function RespondentCodeBox() {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const value = code.trim().toUpperCase();
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      await api("/api/v1/respondent/login", {
        method: "POST",
        body: JSON.stringify({ code: value }),
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      window.location.reload();
    } catch (err) {
      const msg = err instanceof ApiError
        ? (typeof err.body === "object" && err.body && "detail" in err.body
            ? String((err.body as { detail: unknown }).detail)
            : err.status === 404 ? "Код не найден"
            : err.status === 409 ? "Код уже был использован"
            : err.status === 410 ? "Срок кода истёк"
            : "Не удалось войти")
        : "Не удалось войти";
      setError(msg);
      setBusy(false);
    }
  }

  return (
    <div className="mt-10 max-w-[360px] mx-auto text-left">
      <div className="text-[11px] uppercase tracking-[0.08em] text-white/40 mb-2 text-center">или по коду доступа</div>
      <form onSubmit={submit} className="flex gap-2">
        <input
          value={code}
          onChange={(e) => { setCode(e.target.value); setError(null); }}
          placeholder="ABCD2345"
          autoComplete="off"
          spellCheck={false}
          maxLength={12}
          className="flex-1 h-11 px-3 rounded-md bg-white/10 border border-white/20 text-white font-mono tracking-[0.12em] placeholder:text-white/30 uppercase focus:outline-none focus:border-white/40"
        />
        <button type="submit" disabled={busy || !code.trim()}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-white text-ink font-display font-semibold text-[13px] hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed">
          <KeyRound size={14} /> {busy ? "…" : "Войти"}
        </button>
      </form>
      {error && <div className="mt-2 text-[12px] text-[#ff7adb] text-center">{error}</div>}
    </div>
  );
}
